import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import { fromEvent, Observable, Subscription, timer } from 'rxjs';
import { debounceTime, delay, retryWhen, switchMap, tap } from 'rxjs/operators';
import { BaseApiService } from '../../base-service/base-api.service';

export interface ConnectionResponse {
    Browser: boolean;
    InternalSite: boolean;
    Database: boolean;
    ApiVersion: string;
    ServerName: string;
    OutOfDate: boolean;
}

export interface ConnectionServiceOptions {
    heartbeatInterval?: number;
    heartbeatRetryInterval?: number;
    heartbeatUrl: string;
}

@Injectable({
    providedIn: 'root'
})
export class ConnectionService extends BaseApiService implements OnDestroy {
    private stateChangeEventEmitter = new EventEmitter<ConnectionResponse>();

    private currentState: ConnectionResponse = {
        Browser: window.navigator.onLine,
        InternalSite: true,
        Database: true,
        ApiVersion: '',
        ServerName: '',
        OutOfDate: false
    };

    private offlineSubscription: Subscription;
    private onlineSubscription: Subscription;
    private connectionSubscription: Subscription;
    private serviceOptions: ConnectionServiceOptions;
    private originalApiVersion: string;

    constructor() {
        super();

        this.serviceOptions = {
            heartbeatInterval: this.settings.connectionMonitor.refreshInterval,
            heartbeatRetryInterval: this.settings.connectionMonitor.retryInterval,
            heartbeatUrl: 'Foundation/Probe/SQLAlive/Cached'
        };

        this.checkNetworkState();
        this.checkInternetState();
    }

    ngOnDestroy(): void {
        try {
            this.offlineSubscription.unsubscribe();
            this.onlineSubscription.unsubscribe();
            this.connectionSubscription.unsubscribe();
        } catch (e) {}
    }

    private checkNetworkState() {
        this.onlineSubscription = fromEvent(window, 'online').subscribe(() => {
            this.currentState.Browser = true;
            this.checkInternetState();
            this.emitEvent();
        });

        this.offlineSubscription = fromEvent(window, 'offline').subscribe(() => {
            this.currentState.Browser = false;
            this.checkInternetState();
            this.emitEvent();
        });
    }

    private checkInternetState() {
        if (this.connectionSubscription) {
            this.connectionSubscription.unsubscribe();
        }

        this.connectionSubscription = timer(0, this.serviceOptions.heartbeatInterval)
            .pipe(
                switchMap(() => this.get(this.serviceOptions.heartbeatUrl)),
                retryWhen(errors =>
                    errors.pipe(
                        tap(val => {
                            this.currentState.InternalSite = !!(val.error && val.error.validationResults && val.error.validationResults.length > 0);
                            this.currentState.Database = false;
                            this.emitEvent();
                        }),
                        delay(this.serviceOptions.heartbeatRetryInterval)
                    )
                )
            )
            .subscribe(response => {
                if (!!response) {
                    if (!this.originalApiVersion && !!response.apiVersion) {
                        this.originalApiVersion = response.apiVersion;
                    }

                    this.currentState.Database = true;
                    this.currentState.InternalSite = true;
                    this.currentState.ApiVersion = response.apiVersion;
                    this.currentState.ServerName = response.serverName;
                    this.currentState.OutOfDate = response.apiVersion !== this.originalApiVersion;
                }
                this.emitEvent();
            });
    }

    private emitEvent() {
        this.stateChangeEventEmitter.emit(this.currentState);
    }

    monitor(): Observable<ConnectionResponse> {
        return this.stateChangeEventEmitter.pipe(debounceTime(300));
    }
}
